import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, DataUserDataService, PasDataServiceApiConstants, PasRcResolverService } from '@ddt-web/data-service';
import { PartnerPlatformControlsList, PortfolioRetrievalParams, UserDataJsonResponse } from '@ddt-web/domain';
import { PopupErrorObject } from '@ddt-web/error-handler';
import { PartnerAcknowledgementDeeplinkService, PartnerAcknowledgementService, PartnerPortfolioImportService, PartnerPortfoliosDataService, PartnerPortfoliosLoginService } from '@ddt-web/import-partner-portfolios';
import { ApplicationStateStoreProxyService, PartnerPlatform } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { PartnerApiError, PartnerConstants } from '@ddt-web/user-data';
import * as castArray from 'lodash/castArray';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, of, zip } from 'rxjs';
import { first, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PortfolioResolverUtil } from '../utils/portfolio-resolver.util';

/**
* This worker is used to resolve external portfolios, external portfolios are ones whose ids follow this format: `EXT@uniqueId`.
* It will fetch the data to populate the stores if necessary
*/
@Injectable()
export class ExternalPortfolioResolverWorker extends AbstractWorker {
	@InjectConfig(PaConfigKey.SHOW_IMPORT_PARTNER_PORTFOLIO) private readonly showImportPartnerPortfolio: boolean;
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) private readonly partnerPlatformControlsList: PartnerPlatformControlsList;

	private selectedPartner: PartnerPlatform;
	private urlIds: string[];
	private isRefreshNeeded: boolean;
	constructor(
		private dataUserDataService: DataUserDataService,
		private storeService: StoreService,
		private pasRcResolver: PasRcResolverService,
		private router: Router,
		private partnerPortfoliosLoginService: PartnerPortfoliosLoginService,
		private partnerPortfoliosDataService: PartnerPortfoliosDataService,
		private partnerPortfolioImportService: PartnerPortfolioImportService,
		private partnerAcknowledgementService: PartnerAcknowledgementService,
		private partnerAcknowledgementDeeplinkService: PartnerAcknowledgementDeeplinkService,
		private applicationStateProxyService: ApplicationStateStoreProxyService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) {
		super();
	}

	public handle(queryParams: any, overrideOptions: PortfolioRetrievalParams = {}): void {
		this.urlIds = cloneDeep(castArray(queryParams?.portfolios ?? []));
		const externalPortfolioIds = PortfolioResolverUtil.extractExternalPortfolioIds(this.urlIds);

		const portfolioBenchmarkId = queryParams?.acBenchmark;
		if (portfolioBenchmarkId?.includes(PartnerConstants.PARTNER.EXT_IDENTIFIER)) {
			throw new Error(ErrorDCR.PARTNER_UNAUTHORISED_PORTFOLIO_ACCESS);
		}
		if (!isEmpty(externalPortfolioIds)) {
			// Throw error if showImportPartnerPortfolio is false
			if (!this.showImportPartnerPortfolio) {
				throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
			}
			this.setupExternalPortfolios(externalPortfolioIds, overrideOptions);
		}
	}

	private setupExternalPortfolios(externalIds: string[], overrideOptions: PortfolioRetrievalParams): void {
		this.storeService.selectByIds(this.storeConfig.associatedPortfolios, externalIds).pipe(
			take(1),
			withLatestFrom(this.pasRcResolver.getPortfolioRetrievalParams())
		).subscribe(([associatedPortfolios, apiOptions]) => {
			this.isRefreshNeeded = !this.router.navigated;
			this.applicationStateProxyService.updateImportPartnerPortfoliosIsRefreshNeeded(this.isRefreshNeeded);
			this.handleLoadingExternalPortfolio(externalIds, Object.assign(apiOptions, overrideOptions), associatedPortfolios);
			if (this.isRefreshNeeded) {
				// Check if the user has logged in and we have the data in the store
				this.handlePartnerRefreshFlow(externalIds);
			}
		});
	}

	private handleLoadingExternalPortfolio(
		ids: string[],
		apiOptions: PortfolioRetrievalParams,
		associatedPortfolios: any
	): void {
		const externalPortsMissingData = ids.filter(id => !associatedPortfolios.entities[id]);

		// If we have internal portfolios missing data then get them from api
		if (!isEmpty(externalPortsMissingData)) {
			this.storeService.fetch(this.storeConfig.associatedPortfolios);
			this.dataUserDataService.fetchPortfolioWithId(
				externalPortsMissingData,
				this.storeConfig.associatedPortfolios,
				apiOptions
			);
		}
	}

	// TODO gkokkula: divide/simplify this method to make it more readable
	private handlePartnerRefreshFlow(externalPortfolioIds: string[]): void {
		const currentPortfolioIdsInAnalysis = this.urlIds.filter(id => !externalPortfolioIds.includes(id)); // filtering other ids present

		combineLatest([
			this.partnerPortfoliosDataService.retrievePartnerPlatformsList(),
			this.storeService.selectByIds(this.storeConfig.associatedPortfolios, externalPortfolioIds)
		]).pipe(
			first(([platforms, associatedPortfolios]) => platforms.length && !associatedPortfolios.isLoading && !isEmpty(associatedPortfolios.entities)),
			switchMap(([platforms, associatedPortfolios]) => {
				const externalPortfolios: any = Object.values(associatedPortfolios.entities);
				const portfolioPlatforms = [...new Set(externalPortfolios.map(portfolio => portfolio.originPlatform.platform))];

				const platformInfo = platforms.find(platform => platform.id === portfolioPlatforms[0]);
				this.selectedPartner = platformInfo;
				if (!this.partnerPlatformControlsList?.[this.selectedPartner?.id]?.showSavedPortfolios) {
					throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
				}
				return zip(
					this.partnerAcknowledgementService.retrievePartnerAcknowledgementInfo(),
					of(platformInfo),
					of(externalPortfolios)
				);
			}),
			switchMap(([partnerAcknowledgement, platformInfo, externalPortfolios]) => {
				const isUserAcknowledgementRequired = partnerAcknowledgement[this.selectedPartner?.id]?.isUserAcknowledgementRequired;
				if (isUserAcknowledgementRequired) {
					return this.partnerAcknowledgementDeeplinkService.handleDeeplinkAcknowledgement(platformInfo, partnerAcknowledgement).pipe(
						switchMap(response => {
							if (!response[platformInfo.id]?.isUserAcknowledgementRequired) {
								return zip(
									this.partnerPortfoliosLoginService.checkAuthentication(platformInfo),
									of(externalPortfolios),
									this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS)
								);
							} else {
								throw new Error(ErrorDCR.PARTNER_ACKNOWLEDGEMENT_ERROR);
							}
						})
					);
				} else {
					return zip(
						this.partnerPortfoliosLoginService.checkAuthentication(platformInfo),
						of(externalPortfolios),
						this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS)
					);
				}
			}),
			switchMap(([partnerAuthentication, externalPortfolios, rc]) => {
				if (!partnerAuthentication.isAuthenticated || partnerAuthentication.apiErrors) {
					throw new Error(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID);
				} else {
					return this.partnerPortfolioImportService.refreshPartnerPortfolioDeepLink(externalPortfolios, this.selectedPartner, rc, currentPortfolioIdsInAnalysis);
				}
			})
		).subscribe({
			next: (responses: UserDataJsonResponse[]) => {
				if (responses) {
					if (responses.some(response => !response.success)) {
						const errorResponse = responses.find(response => response.success);
						throw new PopupErrorObject(
							{
								...errorResponse,
								message: ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_REFRESH_ERROR
							});
					}
					this.handleSuccessfulPortfolioSave(responses);
					this.applicationStateProxyService.updateImportPartnerPortfoliosIsRefreshNeeded(false);
				}
			},
			error: (error: PartnerApiError) => {
				throw new PopupErrorObject(
					{
						...error,
						message: ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_REFRESH_ERROR
					});
			}
		});
	}

	private handleSuccessfulPortfolioSave(responses: UserDataJsonResponse[]): void {
		const portfoliosToSave = responses.map((response: UserDataJsonResponse) => response.success && response.resultMap?.SEARCH_EDIT_RESULTS[0]?.resultList[0]);
		const partnerIds = portfoliosToSave.map(portfolio => PartnerConstants.PARTNER.EXT_IDENTIFIER + portfolio.uniqueId);

		const partnerName = portfoliosToSave.map(portfolio => portfolio?.originPlatform?.platform).find(platformName => !!platformName);
		GlobalAnalyticsMapperUtil.setImportPartnerName(partnerName);

		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(TealiumConstants.IMPORT_PARTNER_PORTFOLIO_EVENTS.PORTFOLIO_REFRESH, { portfolioIds: partnerIds.join() })
		);
		this.dataUserDataService.updateStoreFromResponse(['associatedPortfolios', 'userPortfolios'], portfoliosToSave, partnerIds);
	}
}
